import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import _ from 'lodash';
import uuidv4 from 'uuid/v4';
import config from '../../../../app/config';
import MessageBox from '../../../../util/messageBox';
import { UtilsMomentJS } from '../../../../store/utilsMoment';
import { useStylesECDS } from './ecdsStyle';
import InformationBox from '../../../../components/other/informationBox';
import useOpenState from '../../../../hooks/useOpenState';
import { AgyleTable, createAgyleTableColumn } from '../../../../components/table/AgyleTable';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import { useDispatch, useSelector } from 'react-redux';
import { setSnackbar } from '../../../../store/reducers/globals';
import { Box, Chip, IconButton, Select } from '@mui/material';
import Typography from '@mui/material/Typography';
import MenuItem from '@mui/material/MenuItem';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import { useTheme } from '@mui/material/styles';
import { getStyles, useStylesAgyleTable } from './referralStyles';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddReferralDialog from './addReferralDialog';
import ReferralsTimeline from './referralsTimeline';
import { renderPriorBox } from './referralsCommon';
import { user } from '../../../../store/reducers/user';

const ReferralsOtherServices = (props) => {
    const { t } = useTranslation();
    const classes = useStylesECDS();
    const classesAT = useStylesAgyleTable();
    const dispatch = useDispatch();
    const [openAddDialog, setOpenAddDialog] = useState(false);
    const theme = useTheme();
    const styles = getStyles(theme);
    const [timelineView, setTimelineView] = useState(null);
    // Props
    const { ecdsData, formContext } = props;
    const userObject = useSelector(user);
    const [list, setList] = useState([]);

    // Validation
    const handleOpenAddDialog = () => {
        setOpenAddDialog(true);
    };

    const handleCloseAddDialog = () => {
        setOpenAddDialog(false);
    };

    //  For Delete
    const [deleteItem, setDeleteItem] = useState(null);
    const [showFilters, setShowFilters] = useState(false);
    const [isConfirmRemoveElementOpen, openConfirmRemoveElement, closeConfirmRemoveElement] = useOpenState(false, true);

    useEffect(() => {
        if (props.onCustomButtons) {
            const buttons = [];
            buttons.push([
                <AgyleButton key="add-button" colour="primary" disabled={props.cannotEdit || timelineView} onClick={handleOpenAddDialog}>
                    {t('+ Add Record')}
                </AgyleButton>,
            ]);
            props.onCustomButtons(buttons);
        }
    }, [props.disableSave, props.cannotEdit, timelineView]);

    // Init data
    useEffect(() => {
        mapToList(
            props.isBB ? props.patient?.specialty?.ReferralsToOtherServices : props?.patient?.studies?.referrals?.ReferralsToOtherServices,
        );
    }, []);

    const mapToList = (values) => {
        const newList = [];
        _.forEach(values, (ref) => {
            let requestTimestamp;
            let assessmentTimestamp;

            if (ref.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare) {
                requestTimestamp = moment
                    .utc(ref.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare, config.ecdsDateTimeFormat)
                    .local();
            } else {
                requestTimestamp = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                    ref.ActivityServiceRequestDate_EmergencyCare,
                    ref.ActivityServiceRequestTime_EmergencyCare,
                );
            }

            if (ref.ReferredToServiceAssessmentTimestamp) {
                assessmentTimestamp = moment.utc(ref.ReferredToServiceAssessmentTimestamp, config.ecdsDateTimeFormat).local();
            } else {
                assessmentTimestamp = UtilsMomentJS.getLocalDateFromUTCECDSFormat(
                    ref.ReferredToServiceAssessmentDate,
                    ref.ReferredToServiceAssessmentTime,
                );
            }

            newList.push({
                id: ref.id === null ? uuidv4() + '' : ref.id,
                ReferredToService_SnomedCt: ref.ReferredToService_SnomedCt || '',
                Reason: ref.Reason || '',
                Acknowledge: ref.Acknowledge || false,
                ActivityServiceRequestDate_EmergencyCare: ref.ActivityServiceRequestDate_EmergencyCare || '',
                ActivityServiceRequestTime_EmergencyCare: ref.ActivityServiceRequestTime_EmergencyCare || '',
                ActivityServiceRequestTimestamp_UrgentAndEmergencyCare: ref.ActivityServiceRequestTimestamp_UrgentAndEmergencyCare || '',
                ReferredToServiceAssessmentDate: ref.ReferredToServiceAssessmentDate || '',
                ReferredToServiceAssessmentTime: ref.ReferredToServiceAssessmentTime || '',
                ReferredToServiceAssessmentTimestamp: ref.ReferredToServiceAssessmentTimestamp || '',
                rTimestamp: requestTimestamp,
                aTimestamp: assessmentTimestamp,
                timestamp: requestTimestamp,
                status: ref.status || '',
                priority: ref.priority || '',
                outcome: ref.outcome,
                history:
                    ref.history?.length > 0
                        ? ref.history
                        : [
                              {
                                  id: 0,
                                  timestamp: requestTimestamp,
                                  user: '-',
                                  type: 'addstatus',
                                  status: 'Requested',
                              },
                          ],
            });
        });
        setList(newList);
    };

    const setDataDictionary = () => {
        let formattedList = _.cloneDeep(list);

        for (const element of formattedList) {
            if (ecdsData) {
                delete element.id;
                element.ReferredToService_SnomedCt = element.ReferredToService_SnomedCt.snomedCode;
            }
            delete element.rTimestamp;
            delete element.aTimestamp;
        }
        const data = { ReferralsToOtherServices: formattedList };
        props.setData(data);
    };

    useEffect(() => {
        setDataDictionary();
    }, [list]);

    const handleItemChange = (data) => {
        if (props.saveData) props.saveData();
        setList((prev) => prev.map((item) => (item.id === data.id ? data : item)));
    };

    const addToList = (dataForm) => {
        if (validation(dataForm)) {
            if (props.saveData) props.saveData();
            const data = {
                id: dataForm.id === null ? uuidv4() + '' : dataForm.id,
                ReferredToService_SnomedCt: dataForm.service || '',
                Reason: dataForm.reason || '',
                priority: dataForm.priority || '',
                Acknowledge: dataForm.acknowledge || false,
                ActivityServiceRequestTimestamp_UrgentAndEmergencyCare: dataForm.requestTimestamp
                    ? moment(dataForm.requestTimestamp).utc().format(config.ecdsDateTimeFormat)
                    : '',
                ActivityServiceRequestDate_EmergencyCare: dataForm.requestTimestamp
                    ? moment(dataForm.requestTimestamp).utc().format(config.ecdsDateFormat)
                    : '',
                ActivityServiceRequestTime_EmergencyCare: dataForm.requestTimestamp
                    ? moment(dataForm.requestTimestamp).utc().format(config.ecdsTimeFormat)
                    : '',
                ReferredToServiceAssessmentTimestamp: dataForm.assessmentTimestamp
                    ? moment(dataForm.assessmentTimestamp).utc().format(config.ecdsDateTimeFormat)
                    : '',
                ReferredToServiceAssessmentDate: dataForm.assessmentTimestamp
                    ? moment(dataForm.assessmentTimestamp).utc().format(config.ecdsDateFormat)
                    : '',
                ReferredToServiceAssessmentTime: dataForm.assessmentTimestamp
                    ? moment(dataForm.assessmentTimestamp).utc().format(config.ecdsTimeFormat)
                    : '',
                rTimestamp: moment(dataForm.requestTimestamp).toISOString(),
                aTimestamp: dataForm.assessmentTimestamp ? moment(dataForm.assessmentTimestamp).toISOString() : '',
                timestamp: moment().toISOString(),
                status: 'Requested',
                history: [
                    {
                        id: 0,
                        timestamp: moment().toISOString(),
                        user: userObject.userName,
                        type: 'addstatus',
                        status: 'Requested',
                    },
                ],
            };
            if (dataForm.initalNote) {
                data.history.push({
                    id: 1,
                    timestamp: moment().toISOString(),
                    user: userObject.userName,
                    type: 'addnote',
                    note: dataForm.initalNote,
                });
            }
            const array = _.cloneDeep(list);

            let index = _.findIndex(array, { id: data.id });
            if (index === -1) array.push(data);
            else array[index] = data;

            setList(array);
            return true;
        }
        return false;
    };

    const validation = (dataForm) => {
        if (dataForm.service) {
            if (!moment(dataForm.requestTimestamp).isValid()) {
                dispatch(
                    setSnackbar({
                        type: 'warning',
                        message: 'The selected Activity Service Request timestamp is incorrect.',
                    }),
                );
                return false;
            }
            return true;
        } else {
            dispatch(setSnackbar({ type: 'warning', message: 'Please complete Referrals to Service field.' }));
            return false;
        }
    };

    const deleteReferral = () => {
        if (props.saveData) props.saveData();
        setList((prevState) => prevState.filter((value) => value.id !== deleteItem?.id));
        setDeleteItem(null);
        closeConfirmRemoveElement();
        setTimelineView(null);
    };

    const renderPriorCol = (column, data) => {
        return renderPriorBox(data, theme);
    };

    const renderOutcomeCol = (column, data) => {
        if (data?.outcome?.length > 0) {
            return (
                <Box
                    sx={{
                        ...styles.priorityCell.outcome,
                        ...styles.priorityCell.common,
                    }}
                >
                    <Typography>{data?.outcome}</Typography>
                </Box>
            );
        }
        return <Typography>{'-'}</Typography>;
    };

    const renderStatusCol = (column, data) => {
        const status = data?.status || (data.Acknowledge ? 'Acknowledged' : 'Requested');
        const statusStyle = status.toLowerCase();

        if (['requested', 'completed', 'acknowledged', 'assessed'].includes(statusStyle)) {
            return (
                <Box sx={{ ...styles.statusCell.common, ...styles.statusCell[statusStyle] }}>
                    <Box sx={styles.statusCell.dot} />
                    <Typography>{status}</Typography>
                </Box>
            );
        }

        return <Typography>{'-'}</Typography>;
    };

    const handleEditClick = (data) => {
        setTimelineView(data);
    };
    const handleDeleteClick = (data) => {
        setDeleteItem(data);
        openConfirmRemoveElement();
    };
    const renderActionButtons = (column, data) => {
        return (
            <Box sx={styles.actions}>
                {/*<IconButton size={'small'} onClick={() => handleTimeLineClick(data)}>*/}
                {/*    <RemoveRedEyeOutlinedIcon />*/}
                {/*</IconButton>*/}
                <IconButton size={'small'} onClick={() => handleEditClick(data)}>
                    <EditIcon />
                </IconButton>
                <IconButton size={'small'} onClick={() => handleDeleteClick(data)}>
                    <DeleteIcon />
                </IconButton>
            </Box>
        );
    };

    const toColumnOptions = (options) => {};

    const renderTable = () => {
        const COLUMNS = [
            createAgyleTableColumn('no', 'NO.', 1, null, 'count', 'notSort', 'medium', 'left', 'custom', false, null, null, null),
            createAgyleTableColumn(
                'reff',
                'REFERRED TO SERVICE',
                2,
                'ReferredToService_SnomedCt',
                'dict',
                'notSort',
                'medium',
                'left',
                true,
                true,
                null,
                null,
                [{ className: classesAT.blueTextCell }],
            ),
            createAgyleTableColumn(
                'rDate',
                'REQUEST DATE & TIME',
                3,
                'rTimestamp',
                'date',
                '',
                'medium',
                'left',
                true,
                true,
                null,
                null,
                null,
                true,
                { type: 'timerange' },
            ),
            createAgyleTableColumn('reason', 'REASON', 5, 'Reason', 'string', 'notSort', 'medium', 'left', true, false, null, null, [
                { className: classesAT.blueTextCell },
            ]),
            createAgyleTableColumn(
                'priority',
                'PRIORITY',
                6,
                'priority',
                '$custom',
                '',
                'medium',
                'left',
                true,
                true,
                null,
                null,
                [renderPriorCol, { className: classesAT.blueTextCell }],
                true,
                {
                    type: 'select',
                    options: ['Urgent', 'Emergency', 'Routine'].map((option) => ({
                        id: option,
                        name: option,
                    })),
                },
            ),
            createAgyleTableColumn(
                'status',
                'STATUS',
                7,
                'status',
                '$custom',
                'notSort',
                'medium',
                'left',
                true,
                true,
                null,
                null,
                [renderStatusCol],
                true,
                {
                    type: 'select',
                    options: ['Requested', 'Acknowledged', 'Assessed', 'Completed'].map((option) => ({
                        id: option,
                        name: option,
                    })),
                },
            ),
            createAgyleTableColumn('outcome', 'OUTCOME', 8, 'outcome', '$custom', 'notSort', 'medium', 'left', true, false, null, null, [
                renderOutcomeCol,
            ]),
        ];

        if (!ecdsData)
            COLUMNS.push(
                createAgyleTableColumn('action', 'ACTION', 9, null, '$custom', 'notSort', 'medium', 'right', true, false, null, null, [
                    renderActionButtons,
                ]),
            );

        if (list) {
            return (
                <AgyleTable
                    columns={COLUMNS}
                    data={list}
                    headerCellClass={classesAT.headerRowCell}
                    headerClass={classesAT.headerRow}
                    tableContainerClass={classesAT.tableContainer}
                    rowClass={classesAT.tableRow}
                    edRefresh={false}
                    showFilters={showFilters}
                    disableFilter
                    onClick={(item, e) => {
                        setTimelineView(item);
                    }}
                />
            );
        }
    };

    return (
        <div>
            {ecdsData && !_.isEmpty(list) && <div style={{ padding: '5px 0px' }}>{renderTable()}</div>}
            {!ecdsData && timelineView && (
                <ReferralsTimeline
                    item={timelineView}
                    onClose={() => setTimelineView(null)}
                    onChange={handleItemChange}
                    userObject={userObject}
                    onDelete={handleDeleteClick}
                />
            )}
            {!ecdsData && !timelineView && (
                <>
                    <Box sx={styles.wrapper}>
                        <Box sx={styles.refTableHaeder}>
                            <Box sx={styles.headLeft}>
                                <Typography variant="body2">Table</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    (4 records)
                                </Typography>
                            </Box>

                            <Box sx={styles.headRight}>
                                <IconButton size={'small'} onClick={() => setShowFilters(!showFilters)}>
                                    <FilterAltIcon />
                                </IconButton>
                            </Box>
                        </Box>

                        <Box sx={styles.tableWrapper}>{renderTable()}</Box>
                    </Box>
                    <AddReferralDialog
                        open={openAddDialog}
                        onClose={handleCloseAddDialog}
                        onSave={addToList}
                        patient={props.patient}
                        checkLenght={props.checkLenght}
                    />
                </>
            )}

            {ecdsData && _.isEmpty(list) && <InformationBox type="info">{t('Specialty referrals have not been set up')}</InformationBox>}

            <MessageBox
                open={isConfirmRemoveElementOpen}
                onClose={closeConfirmRemoveElement}
                title={t('Confirmation')}
                message={t('Do you really want to remove this item?')}
                haveAccept={{ yes: t('Submit'), no: t('Close') }}
                onAccept={deleteReferral}
                warning
            />
        </div>
    );
};

export default ReferralsOtherServices;
